// projectTableData.ts

import { API_BASE_PATH } from "../../environment";

// Define the project base URL separately
const PROJECT_BASE_URL = `${API_BASE_PATH}/project`;

export interface ProjectMapperData {
  project_member: string | null;
  project_lead: string | null;
  project_manager: string | null;
  employee_id: string | null;
}

export interface ProjectAPIResponse {
  project_id: string;
  project_name: string;
  project_logo: string | null;
  project_version: string;
  client_id: string;
  start_date: string;
  end_date: string;
  priority: string;
  price_type: string;
  status: string;
  project_value: number;
  project_type: string;
  description: string;
  upload_file: string;
  tags: (string | null)[] | null;
  Project_progress: number;
  remarks: string;
  estimated_hours: number;
  total_hours: number;
  actual_hours: number;
  deadline: string;

  client_data: {
    client_id: string;
    email: string;
    username: string;
    client_type: string;
  };

  project_mapper_data: ProjectMapperData[];
}

export interface ProjectTableData {
  key: string;
  ProjectID: string;
  ProjectName: string;
  ProjectType: string;
  Client: string;
  Work: string;
  Leader: string;
  Client_ID: string;
  Team: string;
  UserImg: string;
  share: string[];
  Deadline: string;
  Priority: string;
  Status: string;

  // New mapped fields
  ProjectMembers: string[];
  ProjectLeads: string[];
  ProjectManagers: string[];
  EmployeeIDs: string[];
}

// Utility function to format deadline (YYYY-MM-DD -> DD/MM/YYYY)
const formatDate = (dateStr: string): string => {
  const [year, month, day] = dateStr.split("-");
  return `${day}/${month}/${year}`;
};

export const createProject = async (
  formData: FormData
): Promise<ProjectAPIResponse | null> => {
  try {
    const response = await fetch(`${PROJECT_BASE_URL}/`, {
      method: "POST",
      body: formData,
    });

    if (!response.ok) {
      console.error("Failed to create project:", await response.text());
      return null;
    }

    const data: ProjectAPIResponse = await response.json();
    return data;
  } catch (error) {
    console.error("Error creating project:", error);
    return null;
  }
};

// Main transformation function
export const transformProjectData = (
  apiData: ProjectAPIResponse[]
): ProjectTableData[] => {
  return apiData.map((project, index) => {
    const mappers = project.project_mapper_data || [];

    const projectMembers = mappers
      .map((m) => m.project_member)
      .filter((val): val is string => !!val);

    const projectLeads = mappers
      .map((m) => m.project_lead)
      .filter((val): val is string => !!val);

    const projectManagers = mappers
      .map((m) => m.project_manager)
      .filter((val): val is string => !!val);

    const employeeIds = mappers
      .map((m) => m.employee_id)
      .filter((val): val is string => !!val);

    return {
      key: String(index + 1),
      ProjectID: project.project_id,
      ProjectName: project.project_name,
      ProjectType: project.project_type,
      Client: project.client_data?.username || "N/A",
      Work: project.client_data?.client_type || "N/A",
      Leader: project.project_mapper_data[0]?.project_lead || "N/A",
      Client_ID: project.client_data?.client_id || "N/A",
      Team: "+1",
      UserImg: "user-39.jpg",
      share: ["avatar-01.jpg", "avatar-02.jpg", "avatar-03.jpg"],
      Deadline: formatDate(project.deadline),
      Priority: project.priority,
      Status: project.status,

      // New fields
      ProjectMembers: projectMembers,
      ProjectLeads: projectLeads,
      ProjectManagers: projectManagers,
      EmployeeIDs: employeeIds,
    };
  });
};

// projectTableData.ts
export const fetchProjectTableData = async (): Promise<ProjectTableData[]> => {
  try {
    const response = await fetch(`${PROJECT_BASE_URL}/projects`);
    const data: ProjectAPIResponse[] = await response.json();
    const transformed = transformProjectData(data);
    return transformed;
  } catch (error) {
    console.error("Error fetching project data:", error);
    return [];
  }
};

export const deleteProject = async (projectId: string): Promise<boolean> => {
  try {
    const response = await fetch(`${PROJECT_BASE_URL}/${projectId}`, {
      method: "DELETE",
    });

    if (!response.ok) {
      const errorText = await response.text();
      console.error("Failed to delete project:", errorText);
      return false;
    }
    return true;
  } catch (err) {
    console.error("Error deleting project:", err);
    return false;
  }
};

// ✅ Get project by ID
export const getProjectById = async (
  projectId: string
): Promise<ProjectAPIResponse | null> => {
  try {
    const response = await fetch(`${PROJECT_BASE_URL}/project/${projectId}`, {
      method: "GET",
      headers: {
        Accept: "application/json",
      },
    });

    if (!response.ok) {
      const errorText = await response.text();
      console.error("Failed to fetch project:", errorText);
      return null;
    }

    const data: ProjectAPIResponse = await response.json();
    return data;
  } catch (error) {
    console.error("Error fetching project:", error);
    return null;
  }
};

// ✅ Update existing project by ID
export const updateProjectById = async (
  projectId: string,
  formData: FormData
): Promise<ProjectAPIResponse | null> => {
  try {
    const response = await fetch(`${PROJECT_BASE_URL}/${projectId}`, {
      method: "PUT",
      body: formData,
    });

    if (!response.ok) {
      const errorText = await response.text();
      console.error("Failed to update project:", errorText);
      return null;
    }
    const updatedProject: ProjectAPIResponse = await response.json();
    return updatedProject;
  } catch (error) {
    console.error("Error updating project:", error);
    return null;
  }
};

export const getAllProjects = async (
  employeeIds?: string[]
): Promise<ProjectAPIResponse[] | null> => {
  try {
    const response = await fetch(`${PROJECT_BASE_URL}/projects`, {
      method: "GET",
      headers: {
        Accept: "application/json",
      },
    });

    if (!response.ok) {
      const errorText = await response.text();
      console.error("Failed to fetch all projects:", errorText);
      return null;
    }

    const data: ProjectAPIResponse[] = await response.json();

    // If no filtering needed, return all
    if (!employeeIds || employeeIds.length === 0) {
      return data;
    }

    const filtered = data.filter((project) =>
      project.project_mapper_data?.some((m) =>
        [m.project_member, m.project_lead, m.project_manager]
          .filter(Boolean)
          .some((roleId) => roleId && employeeIds.includes(roleId.toString()))
      )
    );

    return filtered;
  } catch (error) {
    console.error("Error fetching all projects:", error);
    return null;
  }
};
